import { useMemo } from "react";
import { openItem } from "@/store/uiSlice";
import Price from "@/components/Price";
import {
  ColumnFilled,
  ColumnFree,
  DefaultFilled,
  DefaultFree,
  MediaFilled,
  DefaultText,
  ListText,
  PriceDownText,
  PriceUpText,
  SquareMiddlePrice,
} from "./preview-widget/components";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Content, OrderItem } from "@/types";
import { getOrderItemPrice } from "@/utils/order";
import { sanitizeNumber } from "@/utils/numbers";
import { useNavigate, useParams } from "react-router-dom";
import parse from "html-react-parser";
import useRoutes from "@/hooks/useRoutes";
import { getThumbnailImage } from "@/utils/media";

type TemplateVariant =
  | "default-text"
  | "list-text"
  | "price-down-text"
  | "price-up-text"
  | "default-filled"
  | "default-free"
  | "column-filled"
  | "column-free"
  | "media-filled"
  | "square-middle-price";

interface Props {
  template: TemplateVariant;
  dish: Content;
}

const DishTemplate = ({ dish, template = "default-filled" }: Props) => {
  const activeOrganization = useAppSelector((store) => store.ui.activeOrganization);
  const activeBranch = useAppSelector((store) => store.ui.activeBranch);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [_, getUrl] = useRoutes();
  const { menuId } = useParams();

  const handleClick = (e: any) => {
    e.stopPropagation();

    dispatch(openItem(dish));

    const qs = new URLSearchParams(window.location.search);
    qs.set("itemId", dish.id);

    const DOMAIN = document.location.host;
    const isBitelink = DOMAIN.includes("bitelink.app");
    const isDevMode = DOMAIN.includes("localhost");

    if (menuId && (isBitelink || isDevMode)) {
      const url = getUrl("organizations.menu.read", { menuId: String(menuId), dishId: dish.id }, false);
      navigate(url + "?" + qs.toString());
    } else {
      navigate(window.location.pathname + "?" + qs.toString());
    }
  };

  const Component = useMemo(() => {
    switch (template) {
      case "default-text":
        return DefaultText;
      case "list-text":
        return ListText;
      case "price-down-text":
        return PriceDownText;
      case "price-up-text":
        return PriceUpText;
      case "default-filled":
        return DefaultFilled;
      case "default-free":
        return DefaultFree;
      case "column-filled":
        return ColumnFilled;
      case "column-free":
        return ColumnFree;
      case "media-filled":
        return MediaFilled;
      case "square-middle-price":
        return SquareMiddlePrice;
      default:
        return null;
    }
  }, [template]);

  const computedDescription = useMemo(() => {
    if (dish?.previewDescription && typeof dish?.previewDescription === "string") {
      return dish.previewDescription;
    }

    if (dish?.description) {
      return parse(dish.description);
    }

    return "";
  }, [dish?.description, dish?.previewDescription]);

  const isDisabled = useMemo(() => {
    return Boolean(dish?.isDisabled) || Boolean(activeBranch && dish?.branchSettings?.[activeBranch]?.isDisabled);
  }, [dish?.isDisabled, dish?.branchSettings, activeBranch]);

  return (
    <Component
      title={dish.title}
      description={computedDescription}
      price={<Price currency="NIO">{dish?.price != 0 ? getOrderItemPrice(dish as unknown as OrderItem) : sanitizeNumber(dish?.startingPrice)}</Price>}
      image={getThumbnailImage({ file: dish?.images, organizationId: activeOrganization, size: "150x150" })}
      onClick={handleClick}
      isDisabled={Boolean(isDisabled)}
      schedule={dish?.schedule}
    />
  );
};

export default DishTemplate;
